/**
 * SEC-2 · Filtro de nombres y chat. DOM-free y compartido.
 *
 * Vive en `@tak/sim` a propósito: el CLIENTE filtra para dar feedback inmediato
 * (el jugador ve al instante que su nombre no vale) y el SERVIDOR vuelve a
 * filtrar porque el cliente no es de fiar — un socket crudo puede mandar
 * `?pname=<lo que sea>`. Una sola implementación evita que las dos reglas
 * diverjan.
 *
 * Alcance deliberado: longitud, caracteres de control/invisibles, espacios
 * abusivos y una lista base de insultos (ES/EN) tolerante a leetspeak. NO es
 * un clasificador; es el filtro barato que quita el grueso del ruido sin pedir
 * un servicio externo. La lista se amplía editando `BANNED`.
 */

/** Longitud máxima del nombre visible en partida. */
export const MAX_NAME = 24;
/** Longitud máxima de un mensaje de chat. */
export const MAX_CHAT = 120;
/**
 * Longitud máxima del nombre de un EQUIPO. Más corta que la de un jugador
 * porque va en el marcador, y ahí un nombre largo desequilibra la composición
 * (o hay que recortarlo con puntos suspensivos, que es peor).
 */
export const MAX_TEAM_NAME = 14;
/** Nombre que se asigna cuando el propuesto queda vacío o es íntegramente vetado. */
export const FALLBACK_NAME = "Jugador";
/** Nombres de equipo por defecto (los colores de los discos: es lo que se ve). */
export const DEFAULT_TEAM_NAMES: [string, string] = ["ROJO", "TURQUESA"];

/**
 * Raíces vetadas (se normalizan igual que el texto a juzgar). Se buscan como
 * SUBCADENA de cada palabra para pillar derivados y plurales ("putas",
 * "hijoputa"). El precio de esa laxitud son los falsos positivos del tipo
 * "diputado": para eso está `SAFE_WORDS`.
 *
 * Criterio para entrar aquí: que la raíz NO aparezca dentro de palabras
 * corrientes. Por eso quedan fuera casos como "coño" (normaliza a "cono", que
 * está dentro de "conocer") o "rape" (dentro de "grape"): en un comparador por
 * subcadena harían más daño que bien.
 */
const BANNED = [
  // ES
  "puta", "puto", "mierda", "cabron", "gilipollas", "polla", "joder", "pendejo",
  "verga", "chinga", "maricon", "zorra", "culiao", "conchatumadre", "malparido",
  "pajero", "boludo", "mogolico", "subnormal", "retrasado",
  // EN
  "fuck", "shit", "bitch", "asshole", "cunt", "nigger", "nigga", "faggot",
  "whore", "slut", "pussy",
];

/**
 * Palabras legítimas que CONTIENEN una raíz vetada. Se comprueban antes que
 * `BANNED`, así "diputado" o "computadora" no se censuran. Van ya normalizadas
 * (ver `normalizeWord`: minúsculas, sin acentos, sin repeticiones).
 */
const SAFE_WORDS = new Set([
  "diputado", "diputados", "diputada", "diputadas", "disputa", "disputas", "disputar",
  "reputacion", "computa", "computadora", "computador", "amputar", "amputa",
]);

/**
 * Invisibles y controles que se eliminan siempre: son la herramienta clásica
 * para colar nombres duplicados ("Ana" vs "A​na") o romper el layout.
 */
const INVISIBLE = /[\u0000-\u001F\u007F-\u009F\u00AD\u200B-\u200F\u2028\u2029\u202A-\u202E\u2060-\u2064\uFEFF]/g;

/** Diacríticos combinantes (los deja la descomposición NFD). */
const COMBINING = /[\u0300-\u036F]/g;

/** Mapa de leetspeak → letra, para que "p4t0" no se cuele como "pato". */
const LEET: Record<string, string> = {
  "0": "o", "1": "i", "3": "e", "4": "a", "5": "s", "7": "t", "8": "b",
  "@": "a", "$": "s", "|": "i", "!": "i", "+": "t", "(": "c",
};

/**
 * Normaliza una palabra para comparar: minúsculas, sin acentos, leetspeak
 * resuelto, repeticiones colapsadas ("puuuta" → "puta") y solo letras/dígitos.
 */
function normalizeWord(word: string): string {
  const noAccents = word.normalize("NFD").replace(COMBINING, "").toLowerCase();
  let out = "";
  let prev = "";
  for (const ch of noAccents) {
    const mapped = LEET[ch] ?? ch;
    if (!/[a-z0-9]/.test(mapped)) continue;
    if (mapped === prev) continue; // colapsa repeticiones
    out += mapped;
    prev = mapped;
  }
  return out;
}

/** Raíces vetadas ya normalizadas (se calcula una vez, no por comparación). */
const BANNED_NORM = BANNED.map(normalizeWord);

/** ¿Esta palabra suelta contiene una raíz vetada (y no está en la lista segura)? */
export function isProfaneWord(word: string): boolean {
  const n = normalizeWord(word);
  if (n.length < 3) return false;
  if (SAFE_WORDS.has(n)) return false;
  return BANNED_NORM.some((bad) => n.includes(bad));
}

/** ¿El texto contiene alguna palabra vetada? */
export function hasProfanity(text: string): boolean {
  return text.split(/\s+/).some((w) => w !== "" && isProfaneWord(w));
}

/**
 * Limpieza base común a nombres y chat: fuera invisibles, espacios colapsados,
 * recorte y tope de longitud. NO juzga el contenido, solo la forma.
 */
export function sanitizeText(raw: string, maxLen: number): string {
  if (typeof raw !== "string") return "";
  return raw
    .replace(INVISIBLE, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLen);
}

/**
 * Saneado de texto de VARIAS líneas.
 *
 * `sanitizeText` aplasta todos los espacios en uno: correcto para un nombre o
 * una línea de chat, desastroso para un texto con forma. Un informe de fallo con
 * sus pasos numerados llegaba convertido en un churro de una línea, y el bloque
 * de diagnóstico igual («build: devsesion: abc»). Aquí se sanea LÍNEA A LÍNEA y
 * se conservan los saltos, con dos topes —longitud y número de líneas— porque el
 * texto sigue viniendo de fuera.
 */
export function sanitizeBlock(raw: unknown, maxLen: number, maxLines = 80): string {
  if (typeof raw !== "string") return "";
  const out: string[] = [];
  for (const linea of raw.split(/\r\n|\r|\n/)) {
    const l = sanitizeText(linea, 400);
    // Una línea en blanco separa párrafos; dos seguidas no separan más.
    if (l === "" && out[out.length - 1] === "") continue;
    out.push(l);
    if (out.length >= maxLines) break;
  }
  return out.join("\n").trim().slice(0, maxLen);
}

/** Reemplaza cada palabra vetada por asteriscos (tope de 12 para no gritar). */
export function maskProfanity(text: string): string {
  return text.replace(/\S+/g, (w) => (isProfaneWord(w) ? "*".repeat(Math.min(w.length, 12)) : w));
}

export type NameReason = "ok" | "empty" | "too-long" | "invalid-chars" | "profanity";

export interface NameVerdict {
  /** Nombre final aceptable (nunca vacío). */
  name: string;
  /** true si hubo que tocar lo propuesto (longitud, invisibles o profanidad). */
  changed: boolean;
  /** Motivo del cambio, para poder avisar al jugador. */
  reason: NameReason;
}

/**
 * Modera un nombre visible. Devuelve SIEMPRE un nombre usable: si lo propuesto
 * queda vacío o es íntegramente profano, cae a `FALLBACK_NAME`; si solo parte
 * lo es, se enmascara. El servidor usa esto como última palabra.
 */
export function moderateName(raw: string): NameVerdict {
  const original = typeof raw === "string" ? raw : "";
  const cleaned = sanitizeText(original, MAX_NAME);
  if (cleaned === "") return { name: FALLBACK_NAME, changed: true, reason: "empty" };

  if (hasProfanity(cleaned)) {
    const masked = maskProfanity(cleaned);
    // Si no queda nada legible tras enmascarar, usa el nombre por defecto.
    const readable = masked.replace(/[*\s]/g, "");
    return { name: readable ? masked : FALLBACK_NAME, changed: true, reason: "profanity" };
  }

  if (cleaned !== original) {
    const tooLong = original.replace(INVISIBLE, "").replace(/\s+/g, " ").trim().length > MAX_NAME;
    return { name: cleaned, changed: true, reason: tooLong ? "too-long" : "invalid-chars" };
  }
  return { name: cleaned, changed: false, reason: "ok" };
}

/**
 * Modera el nombre de un EQUIPO. Mismo filtro que el de jugador (lo escribe una
 * persona y lo ven todas las demás), con su propio tope de longitud y con el
 * nombre por defecto del bando como red de seguridad.
 */
export function moderateTeamName(raw: string, team: 0 | 1): string {
  const cleaned = sanitizeText(raw, MAX_TEAM_NAME);
  if (cleaned === "") return DEFAULT_TEAM_NAMES[team];
  const masked = maskProfanity(cleaned);
  return masked.replace(/[*\s]/g, "") ? masked : DEFAULT_TEAM_NAMES[team];
}

export interface ChatVerdict {
  /** Texto a publicar (enmascarado si hacía falta). Vacío = no publicar nada. */
  text: string;
  /** true si el mensaje no debe publicarse (quedó vacío tras limpiar). */
  blocked: boolean;
  /** true si se censuró alguna palabra. */
  masked: boolean;
}

/**
 * Modera un mensaje de chat: limpia forma, recorta a `MAX_CHAT` y enmascara
 * insultos. No bloquea por profanidad (censura y deja pasar), solo por vacío:
 * tragarse un mensaje entero sin avisar se percibe como un fallo de red.
 */
export function moderateChat(raw: string): ChatVerdict {
  const cleaned = sanitizeText(raw, MAX_CHAT);
  if (cleaned === "") return { text: "", blocked: true, masked: false };
  const masked = maskProfanity(cleaned);
  return { text: masked, blocked: false, masked: masked !== cleaned };
}
